import { T } from 'src/shared/helpers';
import { PropertiesPanelLeafConfig } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';

// IL MAPPING DI OGNI TEXTURE, come nel mockup: un interruttore e, sotto, canale UV, tiling, offset e
// rotazione (utente, 2026-09-15).
//
// NON NASCE NESSUN DATO. Sono campi che ogni texture di Babylon ha già e che il salvataggio della
// scena scrive già — passa dal serializer di Babylon, che li porta tutti. Il pannello vecchio ne
// mostrava due, scalatura e canale UV; offset e rotazione c'erano e non si vedevano.
//
// IL MAPPING DEL MATERIALE INVECE NON C'È (round 2 § 6-bis, voce 12 del round 3): per ora
// l'interruttore scavalca il mapping NEUTRO — tiling 1, offset e rotazione 0, UV0 — cioè quello di
// una texture che nessuno ha toccato. Per questo acceso non è un campo salvato: vuol dire «questa
// texture si discosta dal neutro», e si legge dai valori. Un flag in più sarebbe una seconda verità
// da tenere d'accordo con la prima, e i materiali fatti prima si aprirebbero tutti spenti.

type Transform = { uScale: number; vScale: number; uOffset: number; vOffset: number; wAng: number; coordinatesIndex: number };

const FIELDS: (keyof Transform)[] = ['uScale', 'vScale', 'uOffset', 'vOffset', 'wAng', 'coordinatesIndex'];

const read = (texture: BABYLON.Texture): Transform => ({
    uScale: texture.uScale,
    vScale: texture.vScale,
    uOffset: texture.uOffset,
    vOffset: texture.vOffset,
    wAng: texture.wAng,
    coordinatesIndex: texture.coordinatesIndex
});

const write = (texture: BABYLON.Texture, values: Transform) =>
    FIELDS.forEach((field) => {
        texture[field] = values[field];
    });

const same = (a: Transform, b: Transform) => FIELDS.every((field) => Math.abs(a[field] - b[field]) < 1e-6);

// IL SEGNO DELLA SCALA NON È UN TILING. Un -1 è un ribaltamento — quello che il core fa quando
// rovescia la V di un modello — e nel campo va letto 1: il neutro di quella texture è -1, non 1.
// Si ricorda la prima volta che la texture si vede, così un tiling negativo scritto a mano resta
// negativo invece di tornare positivo al giro dopo.
const signs = new WeakMap<BABYLON.Texture, { u: number; v: number }>();

const signOf = (texture: BABYLON.Texture) => {
    const known = signs.get(texture);
    if (known) return known;
    const sign = { u: Math.sign(texture.uScale) || 1, v: Math.sign(texture.vScale) || 1 };
    signs.set(texture, sign);
    return sign;
};

// ACCESO A MANO RESTA ACCESO finché la pagina è aperta, anche sui valori neutri: dedotto e basta,
// ricadrebbe su spento nel momento stesso in cui lo si accende, prima di aver scritto un numero.
// Riaprendo la pagina un mapping neutro nasce spento, ed è giusto: non scavalca niente.
const overriddenByHand = new Set<string>();

// SPEGNERE RIPORTA AL NEUTRO (utente, 2026-09-15), e i valori di prima restano qui: riaccendendo
// finché la pagina è aperta tornano — lo stesso patto del commutatore ORM/Separate. Nascondere
// soltanto i campi lascerebbe un tiling attivo sotto un interruttore che dice di no.
const valuesBeforeOff = new Map<string, Transform>();

const RADIANS = Math.PI / 180;

/**
 * Il blocco del mapping della texture `type` del materiale. `null` dove la texture non ha un piano
 * — una cube map — e lì restano le righe del pannello vecchio.
 */
export const textureMapping = (material: BABYLON.Material, type: string, changed: () => void): PropertiesPanelLeafConfig | null => {
    const texture = (material as any)[type + 'Texture'];
    if (!(texture instanceof BABYLON.Texture)) return null;

    const key = material.uniqueId + ':' + type;
    const sign = signOf(texture);
    const neutral: Transform = { uScale: sign.u, vScale: sign.v, uOffset: 0, vOffset: 0, wAng: 0, coordinatesIndex: 0 };

    const set = (field: keyof Transform, value: number) => {
        texture[field] = value;
        changed();
    };

    return {
        type: 'texture-mapping',
        id: type + '-texture-mapping',
        label: T._('Override material mapping', 'Admin'),
        overridden: overriddenByHand.has(key) || !same(read(texture), neutral),
        onOverride: (on) => {
            if (on) {
                overriddenByHand.add(key);
                const saved = valuesBeforeOff.get(key);
                if (saved) write(texture, saved);
                valuesBeforeOff.delete(key);
            } else {
                overriddenByHand.delete(key);
                if (!same(read(texture), neutral)) valuesBeforeOff.set(key, read(texture));
                write(texture, neutral);
            }
            changed();
        },
        // UV0 e UV1 sono i due canali che un modello porta quasi sempre; un terzo si offre solo se la
        // texture lo usa già, perché toglierlo dal menu vorrebbe dire non poterlo più leggere.
        uvSets: Array.from(new Set([0, 1, texture.coordinatesIndex])).sort((a, b) => a - b),
        uvSet: { value: texture.coordinatesIndex, onChange: (value) => set('coordinatesIndex', value) },
        tiling: [
            { value: texture.uScale * sign.u, onChange: (value) => set('uScale', value * sign.u) },
            { value: texture.vScale * sign.v, onChange: (value) => set('vScale', value * sign.v) }
        ],
        offset: [
            { value: texture.uOffset, onChange: (value) => set('uOffset', value) },
            { value: texture.vOffset, onChange: (value) => set('vOffset', value) }
        ],
        rotation: { value: texture.wAng / RADIANS, onChange: (degrees) => set('wAng', degrees * RADIANS) }
    };
};
